import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { marketplaceService } from '../../services/marketplaceService';
import {
  PricingConflictError,
  distributorLabel,
  formatPrice,
  hasPrice,
  matchesLicenceQuery,
  parsePrice,
  sameNamedPricedLicences,
  toTermPrices,
  toTermPriceText,
  type CustomerMicrosoftLicencePricing as CustomerPricing,
  type CustomerMicrosoftLicenceRow,
  type CustomerMicrosoftLicenceSetting,
  type MicrosoftLicenceReference,
  type TermPriceText,
} from '../microsoftLicencePricing';

const MAX_SEARCH_RESULTS = 20;

interface DraftRow {
  /** Empty means the customer's default distributor. */
  distributor: string;
  prices: TermPriceText;
}

interface Props {
  customerId: string;
  customerName?: string;
  /** The customer's "Show Microsoft licences" switch. */
  licencesShown: boolean;
  /** Called after a save, so the page can reload what the customer is offered. */
  onSaved: () => void;
}

function toDraftRows(licences: CustomerMicrosoftLicenceRow[]): Record<string, DraftRow> {
  return Object.fromEntries(
    licences.map((row): [string, DraftRow] => [row.skuId, { distributor: row.distributor ?? '', prices: toTermPriceText(row.prices) }]),
  );
}

/**
 * One customer's Microsoft licence pricing: which distributor they buy through, a distributor
 * for any single licence, and manual prices that beat every list. "Customer pays" is what the
 * marketplace function works out, so it updates when you save.
 */
export function CustomerMicrosoftLicencePricing({ customerId, customerName, licencesShown, onSaved }: Props) {
  const [reference, setReference] = useState<MicrosoftLicenceReference | null>(null);
  const [pricing, setPricing] = useState<CustomerPricing | null>(null);
  const [defaultDistributor, setDefaultDistributor] = useState('');
  const [rows, setRows] = useState<Record<string, DraftRow>>({});
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [addQuery, setAddQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const applyLoaded = (loaded: CustomerPricing) => {
    setPricing(loaded);
    setDefaultDistributor(loaded.defaultDistributor ?? '');
    setRows(toDraftRows(loaded.licences));
    setEdited(new Set());
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setConflict(false);
    setMessage(null);
    try {
      const [loadedReference, loadedPricing] = await Promise.all([
        marketplaceService.getMicrosoftLicenceReference(),
        marketplaceService.getCustomerMicrosoftLicencePricing(customerId),
      ]);
      setReference(loadedReference);
      applyLoaded(loadedPricing);
    } catch (err: unknown) {
      setPricing(null);
      setLoadError(err instanceof Error ? err.message : "Couldn't load this customer's Microsoft licence prices");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const serverRows = useMemo(
    () => new Map((pricing?.licences ?? []).map((row) => [row.skuId, row])),
    [pricing],
  );

  const licenceById = useMemo(
    () => new Map((reference?.licences ?? []).map((licence) => [licence.skuId, licence])),
    [reference],
  );

  const orderedRows = useMemo(
    () => Object.keys(rows)
      .map((skuId) => licenceById.get(skuId))
      .filter((licence): licence is NonNullable<typeof licence> => !!licence)
      .filter((licence) => matchesLicenceQuery(licence, filter))
      .sort((a, b) => a.name.localeCompare(b.name, 'en')),
    [rows, licenceById, filter],
  );

  const addResults = useMemo(() => {
    if (!addQuery.trim() || !reference) return [];
    return reference.licences
      .filter((licence) => licence.kind === 'seat' && !rows[licence.skuId] && matchesLicenceQuery(licence, addQuery))
      .slice(0, MAX_SEARCH_RESULTS);
  }, [addQuery, reference, rows]);

  const partnerDefault = pricing?.partnerDefaultDistributor ?? null;
  const customerDefault = defaultDistributor || partnerDefault;

  const sameNamed = useMemo(() => {
    const listed = Object.keys(rows)
      .map((skuId) => licenceById.get(skuId))
      .filter((licence): licence is NonNullable<typeof licence> => !!licence);
    return sameNamedPricedLicences(listed, (skuId) => {
      const row = rows[skuId];
      const distributor = row.distributor || customerDefault;
      return hasPrice(row.prices)
        || (!!distributor && Object.keys(serverRows.get(skuId)?.listPrices[distributor] ?? {}).length > 0);
    });
  }, [rows, licenceById, customerDefault, serverRows]);

  const markEdited = (skuId: string) => {
    setEdited((current) => new Set(current).add(skuId));
    setMessage(null);
  };

  const updateRow = (skuId: string, patch: Partial<DraftRow>) => {
    setRows((current) => ({ ...current, [skuId]: { ...current[skuId], ...patch } }));
    markEdited(skuId);
  };

  const addLicence = (skuId: string) => {
    setRows((current) => ({ ...current, [skuId]: { distributor: '', prices: {} } }));
    markEdited(skuId);
    setAddQuery('');
  };

  const save = async () => {
    if (!pricing || !reference) return;
    setSaveError(null);
    setConflict(false);
    setMessage(null);

    const invalid = Object.entries(rows).filter(([, row]) => toTermPrices(row.prices).invalid.length > 0);
    if (invalid.length > 0) {
      setSaveError(`Some prices aren't valid amounts (${invalid.map(([skuId]) => licenceById.get(skuId)?.name ?? skuId).join(', ')}). Use numbers like 34.20.`);
      return;
    }

    const licences: Record<string, CustomerMicrosoftLicenceSetting> = {};
    for (const [skuId, row] of Object.entries(rows)) {
      const prices = toTermPrices(row.prices).prices;
      if (row.distributor || Object.keys(prices).length > 0) {
        licences[skuId] = { distributor: row.distributor || null, prices };
      }
    }

    setSaving(true);
    try {
      const saved = await marketplaceService.saveCustomerMicrosoftLicencePricing(customerId, {
        version: pricing.version,
        defaultDistributor: defaultDistributor || null,
        licences,
      });
      applyLoaded(saved);
      setMessage(`Saved. ${customerName || 'The customer'} sees these prices on their next marketplace refresh.`);
      onSaved();
    } catch (err: unknown) {
      setConflict(err instanceof PricingConflictError);
      setSaveError(err instanceof Error ? err.message : "Couldn't save this customer's Microsoft licence prices");
    } finally {
      setSaving(false);
    }
  };

  const dirty = edited.size > 0 || (pricing != null && (pricing.defaultDistributor ?? '') !== defaultDistributor);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading Microsoft licence prices…</p>;
  }

  if (loadError || !pricing || !reference) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        <span>{loadError ?? "Couldn't load this customer's Microsoft licence prices"}</span>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!licencesShown && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Microsoft licences are hidden for this customer. These prices apply once you turn them back on.
        </p>
      )}
      {!partnerDefault && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          You haven&apos;t chosen a default distributor yet. Set one under Marketplace, Microsoft licences, or choose one for this customer.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
          Distributor for this customer
          <select
            value={defaultDistributor}
            onChange={(event) => {
              setDefaultDistributor(event.target.value);
              setMessage(null);
            }}
            className="w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{partnerDefault ? `Your default (${distributorLabel(reference, partnerDefault)})` : 'Your default (not chosen yet)'}</option>
            {reference.distributors.map((distributor) => (
              <option key={distributor.id} value={distributor.id}>{distributor.label}</option>
            ))}
          </select>
        </label>
        <div className="relative">
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
            Add a licence for this customer
            <input
              type="search"
              value={addQuery}
              onChange={(event) => setAddQuery(event.target.value)}
              placeholder="Search by name or String ID"
              className="w-72 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </label>
          {addResults.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-64 w-96 overflow-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
              {addResults.map((licence) => (
                <li key={licence.skuId}>
                  <button
                    type="button"
                    onClick={() => addLicence(licence.skuId)}
                    className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                  >
                    <span className="text-slate-900">{licence.name}</span>
                    <span className="font-mono text-[11px] text-slate-500">{licence.skuPartNumber}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter the list"
          aria-label="Filter licences"
          className="w-56 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save prices'}
        </button>
      </div>

      {message && <p className="text-sm text-emerald-700">{message}</p>}
      {saveError && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-rose-600">
          <span>{saveError}</span>
          {conflict && (
            <button
              type="button"
              onClick={() => void load()}
              className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
            >
              Reload prices (discards your unsaved changes)
            </button>
          )}
        </div>
      )}

      {orderedRows.length === 0 ? (
        <p className="text-sm text-slate-500">
          No Microsoft licences are priced for this customer yet. Price them under Marketplace, Microsoft licences, or add one above
          to set a price for this customer only.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {orderedRows.map((licence) => {
            const row = rows[licence.skuId];
            const server = serverRows.get(licence.skuId);
            const effectiveDistributor = row.distributor || customerDefault;
            const listPrices = (effectiveDistributor && server?.listPrices[effectiveDistributor]) || {};
            const rowEdited = edited.has(licence.skuId) || (pricing.defaultDistributor ?? '') !== defaultDistributor;
            const alsoOffered = sameNamed.get(licence.skuId);
            return (
              <li key={licence.skuId} className="space-y-3 px-3 py-3 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{licence.name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{licence.skuPartNumber}</div>
                    {!licence.storefront && (
                      <div className="mt-0.5 text-[11px] text-slate-500">Shown only if this customer already has it</div>
                    )}
                    {alsoOffered && (
                      <div className="mt-0.5 text-[11px] text-amber-700">
                        {customerName || 'This customer'} also gets {alsoOffered.map((other) => other.skuPartNumber).join(', ')},
                        which Microsoft names the same, so they see this product twice. Offer only the one you sell.
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {rowEdited ? (
                      <span className="text-[11px] text-amber-700">Save to update what the customer pays</span>
                    ) : (
                      (row.distributor || Object.values(row.prices).some((value) => value.trim())) && (
                        <button
                          type="button"
                          onClick={() => updateRow(licence.skuId, { distributor: '', prices: {} })}
                          className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                        >
                          Clear
                        </button>
                      )
                    )}
                    <select
                      value={row.distributor}
                      onChange={(event) => updateRow(licence.skuId, { distributor: event.target.value })}
                      aria-label={`${licence.name} distributor`}
                      className="rounded border border-slate-300 px-2 py-1 text-sm"
                    >
                      <option value="">
                        {customerDefault ? `Customer default (${distributorLabel(reference, customerDefault)})` : 'Customer default'}
                      </option>
                      {reference.distributors.map((distributor) => (
                        <option key={distributor.id} value={distributor.id}>{distributor.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {reference.terms.map((term) => {
                    const text = row.prices[term.key] ?? '';
                    const valid = parsePrice(text).valid;
                    const listPrice = listPrices[term.key];
                    const resolved = server?.resolved.terms[term.key];
                    return (
                      <div key={term.key} className="min-w-0">
                        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">{term.label}</div>
                        <input
                          value={text}
                          onChange={(event) => updateRow(licence.skuId, { prices: { ...row.prices, [term.key]: event.target.value } })}
                          placeholder={listPrice ? String(listPrice) : 'No list price'}
                          inputMode="decimal"
                          aria-invalid={!valid}
                          aria-label={`${licence.name} ${term.label} price for this customer`}
                          className={`w-full rounded border px-2 py-1 ${valid ? 'border-slate-300' : 'border-rose-500 bg-rose-50'}`}
                        />
                        {text.trim() && listPrice && (
                          <div className="mt-1 text-[11px] text-slate-500">List {formatPrice(listPrice)}</div>
                        )}
                        {!rowEdited && resolved && (
                          <div className="text-[11px] font-medium text-slate-700">
                            Pays {formatPrice(resolved.price)} ({resolved.source === 'manual' ? 'manual' : `${distributorLabel(reference, server?.resolved.distributor)} list`})
                          </div>
                        )}
                        {!rowEdited && !resolved && <div className="text-[11px] text-slate-500">Not offered</div>}
                      </div>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
